import { BodyConfig } from "@/components/body/parts";
import Utils from "@/components/shared/util";
import { Patient } from "@/store/modules/patient/types";
import { Atom } from "@/store/types";
import _ from "lodash";
import SVG from "svg.js";
import { Store } from "vuex";

export class PartBase {
  config: BodyConfig;
  globalConfig: any;
  container: SVG.Container;
  svgGroup!: SVG.G;
  store: Store<any>;

  protected readonly noRiskStrokeSettings = {
    "stroke-width": "2px",
    stroke: "#BEBEBE"
  };

  protected readonly atRiskStrokeSettings = {
    "stroke-width": "6px",
    stroke: "#ffa014"
  };

  atom!: Atom;
  dark: boolean = false;
  risk: boolean = false;
  extraData: any;
  isInteractive: boolean = false;
  showGreenGrayOnly: boolean;
  action!: Function;
  showing: boolean = false;
  constructor(config: BodyConfig, container: SVG.Container, store: Store<any>) {
    this.config = config;
    this.container = container;
    this.store = store;
    this.showGreenGrayOnly = this.store.getters["user/showGreenGrayOnly"];
    const globalConfig = this.store.getters["patient/config"];
    const parts = globalConfig.parts;
    this.globalConfig = parts[this.config.groupName];

    if (this.globalConfig.configOverride) {
      Object.assign(this.config, _.cloneDeep(this.globalConfig.configOverride));
    }
  }

  // Defaults to user/showGreenGrayOnly, can be overriden per part here
  setGreenGrayOnly(greenGrayOnly: boolean) {
    this.showGreenGrayOnly = greenGrayOnly;
  }

  setDark(dark: boolean) {
    this.dark = dark;
    this.updateBrightnessMode();
  }
  updateBrightnessMode(): void {}

  getBodyCanvas() {
    return this.container;
  }

  getRisk(patient: Patient) {
    return patient.body.risk.includes(this.config.riskKey);
  }

  getAtom(patient: Patient) {
    if (this.globalConfig.atomOverride !== "") {
      this.config.atomId = this.globalConfig.atomOverride;
    }
    return patient.body.atoms[this.config.atomId];
  }

  setWide(): void {}
  setTall(): void {}
  updateData(patient: Patient | null) {
    if (!this.globalConfig.visible) {
      this.hide();
      return;
    }

    let show = false;
    if (patient) {
      show = true;
      this.atom = this.getAtom(patient);
      if (this.config.hideWithNoAtom && !this.atom) {
        show = false;
      }
      const hasAtomAndAge = this.atom && this.atom.age >= 0;
      if (this.config.hideWithNoAge && !hasAtomAndAge) {
        show = false;
      }
    }

    if (show) {
      this.show();
    } else {
      this.hide();
    }
  }
  getBox() {
    if (this.svgGroup && this.svgGroup.visible()) {
      return this.svgGroup.rbox(this.container);
    }
  }

  getExtraData(patient: Patient): void {}

  animate(animated: boolean): void {}

  getClickTarget(): any {
    return this.svgGroup;
  }

  makeInteractive(action: Function) {
    if (!this.globalConfig.canClick) {
      return;
    }

    const clickTarget = this.getClickTarget();
    if (!clickTarget) {
      return;
    }

    this.isInteractive = true;
    this.action = action;

    // Click handler setup on the whole group or custom target
    clickTarget.click(() => {
      action(this.config.popupComponentName);
    });

    clickTarget.attr("cursor", "pointer");
  }

  protected show() {
    this.showing = true;
    if (this.svgGroup) {
      this.svgGroup.show();
    }
  }

  protected hide() {
    this.showing = false;
    if (this.svgGroup) {
      this.svgGroup.hide();
    }
  }

  destroy(): void {}
}

export default class Part extends PartBase {
  riskElements: SVG.Element[] = [];
  levelElements: SVG.Element[] = [];
  animations: Record<string, SVG.Animation> = {};
  animated: boolean = false;
  cleaningUp: boolean = false;

  constructor(config: BodyConfig, container: SVG.Container, store: Store<any>) {
    super(config, container, store);

    this.svgGroup = SVG.select(
      `#${this.config.groupName}`,
      this.container.node
    ).first() as SVG.G;
    // Body parts are hidden by default
    this.svgGroup.attr("visibility", "visible");

    this.store = store;

    if (this.config.riskIds.length) {
      this.riskElements = SVG.select(
        this.convertToSelector(this.config.riskIds),
        this.container.node
      ).valueOf();
    }

    if (this.config.levelIds.length) {
      this.levelElements = SVG.select(
        this.convertToSelector(this.config.levelIds),
        this.container.node
      ).valueOf();
    }
  }

  animate(animated: boolean) {
    this.animated = animated;
  }

  private convertToSelector(ids: Array<string>) {
    return ids
      .map(id => {
        return `#${id}`;
      })
      .join(", ");
  }

  updateData(patient: Patient | null) {
    super.updateData(patient);
    if (patient) {
      this.getExtraData(patient);
      this.risk = this.getRisk(patient);
      this.atom = this.getAtom(patient);

      this.setLevel();
      this.setRisk();
    }
  }

  private setRisk() {
    const strokeSettings = this.risk
      ? this.atRiskStrokeSettings
      : this.noRiskStrokeSettings;

    this.riskElements.forEach(element => {
      element.attr(strokeSettings);
    });
  }
  private setLevel() {
    if (!this.atom) {
      return;
    }

    const levelColor = Utils.getLevelColor(
      this.atom.level,
      this.showGreenGrayOnly
    );
    this.levelElements.forEach(element => {
      element.attr({ fill: levelColor });
    });
  }

  protected resetPosition() {
    this.svgGroup.move(0, 0);
  }

  private finishAnimations() {
    this.cleaningUp = true;
    if (this.animations) {
      Object.keys(this.animations).forEach(key => {
        const animation = this.animations[key];
        animation.finish();
      });
    }
  }

  destroy() {
    this.finishAnimations();
  }
}
